import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  caseManagerInsetSurfaceSx,
  caseManagerPalette,
  publicBodyFontFamily,
  publicTextFieldSx,
} from './caseManagerStyles';

const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const CaseVisibilityPanel = ({ value, onChange, groups = [], provenance = {} }) => {
  const scheduling = value?.scheduling || {};
  const cohort = value?.cohort || {};

  const groupOptions = useMemo(() => {
    return groups.map((group) => ({
      id: group.id,
      name: group.name,
      label: group.name,
      yearLevel: group.metadata?.yearLevel || group.metadata?.cohortYear || null,
    }));
  }, [groups]);

  const selectedGroups = useMemo(() => {
    if (!Array.isArray(cohort.allowedGroupIds)) {
      return [];
    }
    return cohort.allowedGroupIds
      .map((id) => groupOptions.find((group) => group.id === id) || { id, name: 'Unknown group', label: 'Unknown group' })
      .filter(Boolean);
  }, [cohort.allowedGroupIds, groupOptions]);

  const handleSchedulingChange = (field, nextValue) => {
    onChange({
      ...value,
      scheduling: {
        ...scheduling,
        [field]: nextValue,
      },
    });
  };

  const handleCohortChange = (field, nextValue) => {
    onChange({
      ...value,
      cohort: {
        ...cohort,
        [field]: nextValue,
      },
    });
  };

  const publicationState = value?.publication?.state || 'draft';
  const publicationUpdatedAt = value?.publication?.updatedAt
    ? new Date(value.publication.updatedAt).toLocaleString()
    : null;

  const sectionTitleSx = {
    color: caseManagerPalette.ink,
    fontWeight: 800,
    fontFamily: publicBodyFontFamily,
  };

  return (
    <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 1.5, md: 1.8 }, borderRadius: '16px', boxShadow: 'none' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.4 }}
      >
        <Typography variant="h6" sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
          Visibility & Scheduling
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={`Publication: ${publicationState}`} color={publicationState === 'published' ? 'success' : 'default'} size="small" />
          {publicationUpdatedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated {publicationUpdatedAt}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={1.6}>
        <Grid item xs={12} md={4}>
          <Stack spacing={1.15}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Scheduling window
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={scheduling.alwaysAvailable !== false}
                  onChange={(event) => handleSchedulingChange('alwaysAvailable', event.target.checked)}
                />
              )}
              label="Always available"
              sx={{ m: 0 }}
            />
            {scheduling.alwaysAvailable === false && (
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={12} lg={6}>
                  <TextField
                    label="Opens at"
                    type="datetime-local"
                    value={scheduling.startsAt || ''}
                    onChange={(event) => handleSchedulingChange('startsAt', event.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={publicTextFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={12} lg={6}>
                  <TextField
                    label="Closes at"
                    type="datetime-local"
                    value={scheduling.endsAt || ''}
                    onChange={(event) => handleSchedulingChange('endsAt', event.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={publicTextFieldSx}
                  />
                </Grid>
              </Grid>
            )}
            <Box sx={{ px: 1.4, py: 1.1, borderRadius: '12px', bgcolor: 'rgba(18, 61, 103, 0.05)' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Times are shown in your detected timezone
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {detectedTimezone}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={1.15}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Audience filters
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={cohort.allowAllGroups !== false}
                  onChange={(event) => handleCohortChange('allowAllGroups', event.target.checked)}
                />
              )}
              label="Allow all groups"
              sx={{ m: 0 }}
            />
            {cohort.allowAllGroups === false && (
              <Autocomplete
                multiple
                size="small"
                options={groupOptions}
                value={selectedGroups}
                onChange={(_event, nextValue) => handleCohortChange('allowedGroupIds', nextValue.map((group) => group.id))}
                getOptionLabel={(option) => option.label || option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Allowed groups"
                    placeholder="Select groups"
                    sx={publicTextFieldSx}
                  />
                )}
              />
            )}
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={cohort.allowedYearLevels || []}
              value={cohort.allowedYearLevels || []}
              onChange={(_event, nextValue) => {
                const normalizedLevels = Array.from(new Set(
                  (nextValue || [])
                    .map((level) => (level === null || level === undefined
                      ? null
                      : level.toString().trim().toLowerCase()))
                    .filter(Boolean)
                ));
                handleCohortChange('allowedYearLevels', normalizedLevels);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Allowed year levels"
                  placeholder="Add year level"
                  sx={publicTextFieldSx}
                />
              )}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={1.15}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Notes & provenance
            </Typography>
            <TextField
              label="Access notes"
              multiline
              minRows={2}
              value={value?.accessNotes || ''}
              onChange={(event) => onChange({
                ...value,
                accessNotes: event.target.value,
              })}
              placeholder="Explain restrictions or scheduling context."
              size="small"
              sx={publicTextFieldSx}
            />
            <Box sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              <Typography variant="caption" display="block">
                Origin type: {provenance.originType || 'virtual-medic'}
              </Typography>
              {provenance.originTenantId && (
                <Typography variant="caption" display="block">
                  Origin tenant ID: {provenance.originTenantId}
                </Typography>
              )}
              {provenance.lastSyncedFromOriginAt && (
                <Typography variant="caption" display="block">
                  Last synced: {new Date(provenance.lastSyncedFromOriginAt).toLocaleString()}
                </Typography>
              )}
              {provenance.originNotes && (
                <Typography variant="caption" display="block">
                  Notes: {provenance.originNotes}
                </Typography>
              )}
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CaseVisibilityPanel;
